import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'motion/react';
import { Minimize2 } from 'lucide-react';

export interface FullscreenSlotOverlayProps {
  children: React.ReactNode;
  onClose: () => void;
  portalTarget: HTMLElement;
}

export function FullscreenSlotOverlay({
  children,
  onClose,
  portalTarget,
}: FullscreenSlotOverlayProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="absolute inset-0 z-40 bg-background flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-end px-6 py-3 border-b border-border shrink-0">
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Minimize"
        >
          <Minimize2 className="w-5 h-5" />
        </button>
      </div>

      {/* Content — full width with padding, no max-w constraint */}
      <div className="flex-1 overflow-auto px-8 py-6">
        <div className="space-y-4">
          {children}
        </div>
      </div>
    </motion.div>,
    portalTarget
  );
}
